/**
 * /app/accounting — Tenant-side Accounting hub (Phase 1a).
 * Gated by the `accounting` feature flag.
 *
 * Tabs: Chart of Accounts · Journal · Ledger · Reports (TB / P&L / BS) ·
 *       GST Config · Periods · Health
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fmtDate, fmtDateTime } from "@/lib/time";
import { SearchableSelect } from "@/components/SearchableSelect";
import { api, apiError } from "@/lib/api";
import { useFeatures } from "@/context/FeaturesContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import {
    BookOpen, Plus, Trash2, RotateCcw, CheckCircle2, AlertTriangle,
    Lock, Unlock, BarChart3, FileText, Settings as SettingsIcon, ShieldCheck,
    ShieldAlert, Mail, X, Download, Briefcase, Sparkles, Search, ChevronRight, ChevronDown,
} from "lucide-react";

const TABS = [
    { key: "coa",      label: "Chart of Accounts",   icon: BookOpen },
    { key: "journal",  label: "Journal",              icon: FileText },
    { key: "invoices", label: "Invoices",             icon: FileText },
    { key: "recon",    label: "Reconciliation",       icon: ShieldCheck },
    { key: "ledger",   label: "Ledger",               icon: BarChart3 },
    { key: "reports",  label: "Reports",              icon: BarChart3 },
    { key: "story",    label: "Story mode",           icon: BookOpen },
    { key: "gst",      label: "GST Config",           icon: SettingsIcon },
    { key: "periods",  label: "Periods",              icon: Lock },
    { key: "health",   label: "Health",               icon: ShieldCheck },
];

const fmtINR = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TenantAccounting() {
    const { flags, loading: flagsLoading } = useFeatures();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const wantsLegalModal = params.get("legal") === "1";
    const [tab, setTab] = useState(wantsLegalModal ? "journal" : "reports");
    const [autoOpenLegal, setAutoOpenLegal] = useState(wantsLegalModal);
    const enabled = flags.accounting === true;

    // Show locked banner if disabled
    if (!flagsLoading && !enabled) {
        return (
            <div className="space-y-6" data-testid="tenant-accounting-disabled">
                <div className="border border-border bg-card p-8 text-center">
                    <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-3"/>
                    <h2 className="text-2xl text-foreground mb-2">Accounting module is not active for your plan</h2>
                    <p className="text-muted-foreground mb-4 max-w-xl mx-auto">
                        Professional Accounting (Chart of Accounts · Journal · Ledger · GST Returns · Bank Recon · CA-grade Reports)
                        is part of our top-tier subscription. Contact your firm administrator or upgrade your plan to enable it.
                    </p>
                    <button onClick={() => navigate("/app/settings")}
                        data-testid="acc-go-settings"
                        className="px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-[11px] uppercase tracking-[0.16em]">
                        Go to Settings
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="tenant-accounting">
            <AdminEditRequestBanner/>

            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Firm books</div>
                    <h1 className="text-3xl text-foreground">Accounting</h1>
                    <p className="text-muted-foreground text-sm mt-1">Your firm&apos;s complete double-entry ledger. Fully isolated from the platform&apos;s books.</p>
                </div>
                <CaPackDownloadButton/>
            </div>

            {/* Tabs */}
            <div className="flex flex-nowrap gap-1 border-b border-border overflow-x-auto scrollbar-hide">
                {TABS.map(t => {
                    const Ic = t.icon;
                    return (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            data-testid={`tab-${t.key}`}
                            className={`flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.16em] border-b-2 ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                            <Ic className="w-3 h-3"/>{t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "coa"      && <CoaTab/>}
            {tab === "journal"  && <JournalTab autoOpenLegal={autoOpenLegal} onConsumeAutoOpen={() => setAutoOpenLegal(false)}/>}
            {tab === "recon"    && <ReconciliationTab/>}
            {tab === "invoices" && <InvoicesTab/>}
            {tab === "ledger"   && <LedgerTab/>}
            {tab === "reports"  && <ReportsTab/>}
            {tab === "story"    && <StoryModeTab/>}
            {tab === "gst"      && <GstTab/>}
            {tab === "periods"  && <PeriodsTab/>}
            {tab === "health"   && <HealthTab/>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// COA — Hierarchical, searchable Chart of Accounts.
// Admin-only create / edit / delete (firm_owner, managing_partner, super_admin)
// ---------------------------------------------------------------------------
const ADMIN_ROLES = ["firm_owner", "managing_partner", "super_admin"];

function CoaTab() {
    const { user } = useAuth();
    const isAdmin = ADMIN_ROLES.includes(user?.role);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState(null);  // account being edited
    const [addParent, setAddParent] = useState(null);  // pre-selected parent for "Add child"
    const [query, setQuery] = useState("");
    const [expanded, setExpanded] = useState(() => new Set());

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/accounting/coa"); setRows(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Build top-level → children map
    const { topLevels, childrenOf } = useMemo(() => {
        const top = []; const ch = new Map();
        rows.forEach(r => {
            if (!r.parent_code) top.push(r);
            else {
                const arr = ch.get(r.parent_code) || []; arr.push(r); ch.set(r.parent_code, arr);
            }
        });
        const cmp = (a, b) => a.code.localeCompare(b.code, undefined, { numeric: true });
        top.sort(cmp);
        ch.forEach(arr => arr.sort(cmp));
        return { topLevels: top, childrenOf: ch };
    }, [rows]);

    // Search: when active, surface every matching account AND auto-expand its ancestors.
    const q = query.trim().toLowerCase();
    const matchSet = useMemo(() => {
        if (!q) return null;
        const m = new Set();
        const byCode = new Map(rows.map(r => [r.code, r]));
        rows.forEach(r => {
            if (r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)) {
                m.add(r.code);
                // also mark ancestors so the tree path is visible
                let p = r.parent_code;
                while (p) { m.add(p); const par = byCode.get(p); p = par?.parent_code; }
            }
        });
        return m;
    }, [q, rows]);

    const toggle = (code) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };
    const isOpen = (code) => q ? matchSet?.has(code) : expanded.has(code);

    const handleAddChild = (parent) => { if (!isAdmin) return; setAddParent(parent); setShowAdd(true); };
    const handleEdit = (acc) => { if (!isAdmin) return; setEditing(acc); };

    const renderRow = (r, depth) => {
        const kids = childrenOf.get(r.code) || [];
        const open = isOpen(r.code);
        const visible = !q || matchSet?.has(r.code);
        if (!visible) return null;
        const canAddChild = depth < 2;  // max 3 levels (depth 0,1,2)
        const isMatch = matchSet && (r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q));
        return (
            <div key={r.id}>
                <div className={`flex items-center gap-2 px-2 sm:px-3 py-2 border-b border-border hover:bg-muted/40 ${isMatch ? "bg-primary/5" : ""}`}
                     data-testid={`coa-row-${r.code}`}>
                    <div style={{ paddingLeft: depth * 14 }} className="flex items-center gap-1.5 flex-1 min-w-0">
                        {kids.length > 0 ? (
                            <button onClick={() => toggle(r.code)}
                                data-testid={`coa-toggle-${r.code}`}
                                className="text-muted-foreground hover:text-foreground p-0.5 shrink-0">
                                {open ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
                            </button>
                        ) : <div className="w-4 shrink-0"/>}
                        <span className="font-mono text-primary text-xs shrink-0">{r.code}</span>
                        <span className="text-foreground text-xs truncate min-w-0">{r.name}</span>
                        {r.is_system && <span className="hidden sm:inline text-[9px] uppercase tracking-wider text-muted-foreground border border-border px-1 ml-1 shrink-0">sys</span>}
                        {!r.is_active && <span className="text-[9px] uppercase tracking-wider text-rose-400 border border-rose-500/40 px-1 ml-1 shrink-0">inactive</span>}
                    </div>
                    {/* Desktop-only columns */}
                    <span className="hidden md:inline text-muted-foreground text-[10px] uppercase tracking-wider w-20 text-right">{r.type}</span>
                    <span className="hidden lg:inline text-muted-foreground text-[10px] uppercase tracking-wider w-14 text-right">{r.normal_balance}</span>
                    <span className="hidden lg:inline text-[10px] w-10 text-center">{r.gst_relevant ? <span className="text-primary">GST</span> : <span className="text-muted-foreground">—</span>}</span>
                    {isAdmin && (
                        <div className="flex items-center gap-1 justify-end shrink-0">
                            {canAddChild && (
                                <button onClick={() => handleAddChild(r)}
                                    data-testid={`coa-add-child-${r.code}`}
                                    title={depth === 0 ? "Add sub-ledger" : "Add sub-account"}
                                    aria-label={depth === 0 ? "Add sub-ledger" : "Add sub-account"}
                                    className="text-muted-foreground hover:text-primary p-1.5 border border-border hover:border-primary">
                                    <Plus className="w-3 h-3"/>
                                </button>
                            )}
                            <button onClick={() => handleEdit(r)}
                                data-testid={`coa-edit-${r.code}`}
                                aria-label="Edit"
                                className="text-[9px] uppercase tracking-wider px-2 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary">
                                Edit
                            </button>
                        </div>
                    )}
                </div>
                {open && kids.map(k => renderRow(k, depth + 1))}
            </div>
        );
    };

    return (
        <div className="space-y-3" data-testid="acc-coa-tab">
            <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-3 h-3 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2"/>
                        <input
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Search code or name (e.g. 5810 or Counsel)"
                            data-testid="coa-search"
                            className="w-full bg-card border border-border pl-8 pr-3 py-2 text-foreground text-xs placeholder:text-muted-foreground"
                        />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{rows.length} accounts</span>
                </div>
                {isAdmin && (
                    <button onClick={() => { setAddParent(null); setShowAdd(true); }} data-testid="coa-add"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">
                        <Plus className="w-3 h-3"/>New account
                    </button>
                )}
            </div>
            {!isAdmin && (
                <div className="border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
                    <Lock className="w-3 h-3 inline mr-1 -mt-0.5"/>Only the firm owner or managing partner can create or edit accounts.
                </div>
            )}
            <div className="border border-border bg-card">
                <div className="flex items-center gap-2 px-2 sm:px-3 py-2 border-b border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <div className="flex-1">Account</div>
                    <span className="hidden md:inline w-20 text-right">Type</span>
                    <span className="hidden lg:inline w-14 text-right">Normal</span>
                    <span className="hidden lg:inline w-10 text-center">GST</span>
                    {isAdmin && <span className="text-right">Actions</span>}
                </div>
                {loading && <div className="px-3 py-8 text-center text-muted-foreground text-xs">Loading…</div>}
                {!loading && topLevels.length === 0 && <div className="px-3 py-8 text-center text-muted-foreground text-xs">No accounts found.</div>}
                {!loading && topLevels.map(t => renderRow(t, 0))}
            </div>
            {showAdd && <AccountEditorModal
                mode="create"
                initialParent={addParent}
                allAccounts={rows}
                onClose={() => { setShowAdd(false); setAddParent(null); }}
                onSaved={() => { setShowAdd(false); setAddParent(null); load(); }}
            />}
            {editing && <AccountEditorModal
                mode="edit"
                account={editing}
                allAccounts={rows}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
            />}
        </div>
    );
}

function AccountEditorModal({ mode, account, initialParent, allAccounts, onClose, onSaved }) {
    const isEdit = mode === "edit";
    // Determine starting kind from initialParent depth
    const findDepth = (code) => {
        if (!code) return 0;
        const byCode = new Map(allAccounts.map(a => [a.code, a]));
        let d = 0; let cur = byCode.get(code);
        while (cur) { d += 1; cur = cur.parent_code ? byCode.get(cur.parent_code) : null; }
        return d;
    };
    const parentDepth = initialParent ? findDepth(initialParent.code) : 0;
    const defaultKind = isEdit
        ? (account.parent_code ? (findDepth(account.parent_code) === 1 ? "sub_ledger" : "sub_account") : "account")
        : (initialParent ? (parentDepth === 1 ? "sub_ledger" : "sub_account") : "account");

    const [kind, setKind] = useState(defaultKind);
    const [form, setForm] = useState(() => isEdit ? {
        code: account.code, name: account.name, type: account.type,
        parent_code: account.parent_code || "", normal_balance: account.normal_balance,
        gst_relevant: account.gst_relevant, is_active: account.is_active,
    } : {
        code: "", name: "",
        type: initialParent?.type || "expense",
        parent_code: initialParent?.code || "",
        normal_balance: initialParent?.normal_balance || "debit",
        gst_relevant: false, is_active: true,
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Auto-fetch next-code suggestion when parent changes (create mode only)
    useEffect(() => {
        if (isEdit) return;
        if (kind === "account" && form.code) return;
        const params = kind === "account" ? {} : { parent_code: form.parent_code };
        if (kind !== "account" && !form.parent_code) return;
        api.get("/accounting/coa/next-code", { params }).then(r => {
            if (r.data?.suggested && !form.code) setForm(f => ({ ...f, code: r.data.suggested }));
        }).catch(() => {});
    }, [kind, form.parent_code, isEdit, form.code]);

    // Inherit type / normal_balance from chosen parent
    useEffect(() => {
        if (isEdit) return;
        if (kind === "account") return;
        const par = allAccounts.find(a => a.code === form.parent_code);
        if (par) setForm(f => ({ ...f, type: par.type, normal_balance: par.normal_balance }));
    }, [form.parent_code, kind, isEdit, allAccounts]);

    const setKindAndReset = (k) => {
        setKind(k);
        if (!isEdit) setForm(f => ({ ...f, parent_code: "", code: "" }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/accounting/coa/${account.id}`, {
                    name: form.name, is_active: form.is_active,
                    gst_relevant: form.gst_relevant,
                });
                toast.success("Account updated");
            } else {
                const payload = {
                    code: form.code.trim(), name: form.name.trim(),
                    type: form.type, normal_balance: form.normal_balance,
                    parent_code: form.parent_code || null,
                    gst_relevant: form.gst_relevant,
                };
                await api.post("/accounting/coa", payload);
                toast.success(kind === "account" ? "Account created" : kind === "sub_ledger" ? "Sub-ledger created" : "Sub-account created");
            }
            onSaved();
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!isEdit || account.is_system) return;
        if (!window.confirm(`Delete account ${account.code} · ${account.name}? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            await api.delete(`/accounting/coa/${account.id}`);
            toast.success("Account deleted");
            onSaved();
        } catch (err) { toast.error(apiError(err)); }
        finally { setDeleting(false); }
    };

    const title = isEdit
        ? `Edit ${account.code} · ${account.name}`
        : kind === "account" ? "New top-level account"
        : kind === "sub_ledger" ? "New sub-ledger"
        : "New sub-account";

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="coa-editor-modal">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg text-foreground">{title}</h3>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
                </div>

                {!isEdit && (
                    <div className="grid grid-cols-3 gap-1 border border-border p-0.5" data-testid="coa-kind-picker">
                        {[
                            { k: "account", label: "Account" },
                            { k: "sub_ledger", label: "Sub-ledger" },
                            { k: "sub_account", label: "Sub-account" },
                        ].map(o => (
                            <button key={o.k} type="button" onClick={() => setKindAndReset(o.k)}
                                data-testid={`coa-kind-${o.k}`}
                                className={`text-[10px] uppercase tracking-[0.16em] py-2 ${kind === o.k ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                )}

                {!isEdit && kind !== "account" && (
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">
                            {kind === "sub_ledger" ? "Parent account" : "Parent sub-ledger"}
                        </label>
                        <SearchableSelect
                            value={form.parent_code}
                            onChange={(v) => setForm(f => ({ ...f, parent_code: v }))}
                            options={(kind === "sub_ledger"
                                ? allAccounts.filter(a => !a.parent_code)
                                : allAccounts.filter(a => a.parent_code && !allAccounts.find(gp => gp.code === a.parent_code)?.parent_code)
                            ).map(a => ({ value: a.code, label: `${a.code} · ${a.name} (${a.type})` }))}
                            placeholder="Search & select parent…"
                            testId="coa-parent-select"
                        />
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Code</label>
                        <input value={form.code} onChange={e => setForm({...form, code: e.target.value})}
                            placeholder="e.g. 5810" required disabled={isEdit}
                            data-testid="coa-code"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono disabled:opacity-60"/>
                    </div>
                    <div className="col-span-2">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Account name</label>
                        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                            placeholder="e.g. Senior Counsel Fees" required
                            data-testid="coa-name"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Type</label>
                        <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                            disabled={isEdit || (!isEdit && kind !== "account")}
                            data-testid="coa-type"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm disabled:opacity-60">
                            <option value="asset">Asset</option>
                            <option value="liability">Liability</option>
                            <option value="equity">Equity</option>
                            <option value="income">Income</option>
                            <option value="expense">Expense</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Normal balance</label>
                        <select value={form.normal_balance} onChange={e => setForm({...form, normal_balance: e.target.value})}
                            disabled={isEdit || (!isEdit && kind !== "account")}
                            data-testid="coa-normal"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm disabled:opacity-60">
                            <option value="debit">Debit</option>
                            <option value="credit">Credit</option>
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <input type="checkbox" checked={form.gst_relevant} onChange={e => setForm({...form, gst_relevant: e.target.checked})}
                            data-testid="coa-gst"
                            className="accent-primary"/>
                        GST relevant
                    </label>
                    {isEdit && !account.is_system && (
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                                data-testid="coa-active"
                                className="accent-primary"/>
                            Active
                        </label>
                    )}
                </div>

                {!isEdit && kind !== "account" && !form.parent_code && (
                    <div className="text-[11px] text-amber-400 border border-amber-500/30 bg-amber-500/5 px-2 py-1.5">
                        Select a parent before saving.
                    </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                    <div>
                        {isEdit && !account.is_system && (
                            <button type="button" onClick={handleDelete} disabled={deleting}
                                data-testid="coa-delete"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 disabled:opacity-50">
                                <Trash2 className="w-3 h-3"/>{deleting ? "Deleting…" : "Delete"}
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                        <button type="submit" disabled={saving || (!isEdit && kind !== "account" && !form.parent_code)}
                            data-testid="coa-save"
                            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create")}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------
function JournalTab({ autoOpenLegal = false, onConsumeAutoOpen }) {
    const [rows, setRows] = useState([]);
    const [coa, setCoa] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPost, setShowPost] = useState(false);
    const [showLegal, setShowLegal] = useState(false);

    // Auto-open the Quick Legal Entry modal if landed via /app/accounting?legal=1
    useEffect(() => {
        if (autoOpenLegal) {
            setShowLegal(true);
            onConsumeAutoOpen?.();
        }
    }, [autoOpenLegal, onConsumeAutoOpen]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [j, c] = await Promise.all([api.get("/accounting/journal"), api.get("/accounting/coa")]);
            setRows(j.data); setCoa(c.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const reverse = async (id) => {
        if (!window.confirm("Post a reversing entry for this journal? This is irreversible.")) return;
        try { await api.post(`/accounting/journal/${id}/reverse`); toast.success("Reversed"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const journalCtrl = useTableControls(rows, {
        accessors: { amount: (r) => (r.total_paise || 0) / 100 },
    });

    return (
        <div className="space-y-3" data-testid="acc-journal-tab">
            <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{rows.length} entries</span>
                <div className="flex items-center gap-2">
                    <button onClick={() => setShowLegal(true)} data-testid="journal-legal-entry"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 text-[10px] uppercase tracking-[0.16em]">
                        <Briefcase className="w-3 h-3"/>Quick Legal Entry
                    </button>
                    <button onClick={() => setShowPost(true)} data-testid="journal-post"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">
                        <Plus className="w-3 h-3"/>Post entry
                    </button>
                </div>
            </div>
            <FilterChip ctrl={journalCtrl}/>
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh field="date" ctrl={journalCtrl} className="text-left px-3 py-2">Date</SortableTh>
                            <SortableTh field="narration" ctrl={journalCtrl} className="text-left px-3 py-2">Narration</SortableTh>
                            <SortableTh field="source" ctrl={journalCtrl} className="text-left px-3 py-2">Source</SortableTh>
                            <SortableTh field="amount" ctrl={journalCtrl} className="text-right px-3 py-2" align="right">Amount</SortableTh>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
                        {!loading && journalCtrl.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No entries yet · post your first journal</td></tr>}
                        {!loading && journalCtrl.rows.map(r => (
                            <>
                                <tr key={r.id} className="border-b border-card hover:bg-muted/40">
                                    <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.date)}</td>
                                    <td className="px-3 py-2 text-foreground">{r.narration || <span className="text-muted-foreground">—</span>}</td>
                                    <td className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{r.source}</td>
                                    <td className="px-3 py-2 text-right text-foreground font-mono">{fmtINR((r.total_paise || 0) / 100)}</td>
                                    <td className="px-3 py-2 text-right">
                                        {!r.reversed ? (
                                            <button onClick={() => reverse(r.id)} data-testid={`journal-reverse-${r.id}`}
                                                className="text-[10px] uppercase text-rose-400 hover:underline">Reverse</button>
                                        ) : <span className="text-[10px] uppercase text-muted-foreground">Reversed</span>}
                                    </td>
                                </tr>
                                <tr className="border-b border-card bg-background">
                                    <td colSpan={5} className="px-3 py-1.5 text-[10px] text-muted-foreground">
                                        {r.lines.map((ln, i) => (
                                            <span key={`${ln.account_code}-${i}`} className="mr-3">
                                                <span className="font-mono text-primary">{ln.account_code}</span>
                                                {ln.debit > 0 && <span className="ml-1 text-emerald-400">DR {fmtINR(ln.debit)}</span>}
                                                {ln.credit > 0 && <span className="ml-1 text-rose-400">CR {fmtINR(ln.credit)}</span>}
                                            </span>
                                        ))}
                                    </td>
                                </tr>
                            </>
                        ))}
                    </tbody>
                </table>
            </div>
            {showPost && <PostJournalModal coa={coa} onClose={() => setShowPost(false)} onPosted={() => { setShowPost(false); load(); }} />}
            {showLegal && <LegalEntryModal onClose={() => setShowLegal(false)} onPosted={() => { setShowLegal(false); load(); }} />}
        </div>
    );
}

function PostJournalModal({ coa, onClose, onPosted }) {
    const [narration, setNarration] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [lines, setLines] = useState([
        { account_code: "", debit: "", credit: "", memo: "" },
        { account_code: "", debit: "", credit: "", memo: "" },
    ]);
    const [caseRef, setCaseRef] = useState("");
    const [clientRef, setClientRef] = useState("");
    const [costCenter, setCostCenter] = useState("");
    const [saving, setSaving] = useState(false);

    const setLine = (i, key, val) => {
        const next = [...lines];
        next[i] = { ...next[i], [key]: val };
        setLines(next);
    };
    const addLine = () => setLines([...lines, { account_code: "", debit: "", credit: "", memo: "" }]);
    const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

    const totals = useMemo(() => {
        const dr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
        const cr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
        return { dr, cr, balanced: Math.abs(dr - cr) < 0.01 && dr > 0 };
    }, [lines]);

    const submit = async (e) => {
        e.preventDefault();
        if (!totals.balanced) { toast.error("Entry not balanced"); return; }
        setSaving(true);
        try {
            const payload = {
                narration, date: new Date(date).toISOString(),
                lines: lines.filter(l => l.account_code).map(l => ({
                    account_code: l.account_code,
                    debit: parseFloat(l.debit) || 0,
                    credit: parseFloat(l.credit) || 0,
                    memo: l.memo || undefined,
                })),
                extra: {
                    ...(caseRef ? { case_ref: caseRef } : {}),
                    ...(clientRef ? { client_ref: clientRef } : {}),
                    ...(costCenter ? { cost_center: costCenter } : {}),
                },
            };
            await api.post("/accounting/journal", payload);
            toast.success("Journal posted");
            onPosted();
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-3xl p-5 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="journal-post-modal">
                <h3 className="text-lg text-foreground">Post journal entry</h3>
                <div className="grid grid-cols-2 gap-3">
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                        className="bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    <input value={narration} onChange={e => setNarration(e.target.value)}
                        data-testid="journal-narration"
                        placeholder="Narration" className="bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                </div>
                <div className="space-y-1">
                    {lines.map((ln, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                            <select value={ln.account_code} onChange={e => setLine(i, "account_code", e.target.value)}
                                data-testid={`journal-acc-${i}`}
                                className="col-span-4 bg-background border border-border px-2 py-1.5 text-foreground text-xs">
                                <option value="">Account…</option>
                                {coa.filter(a => a.is_active).map(a => (
                                    <option key={a.id} value={a.code}>{a.code} · {a.name}</option>
                                ))}
                            </select>
                            <input value={ln.debit} onChange={e => setLine(i, "debit", e.target.value)}
                                data-testid={`journal-dr-${i}`}
                                placeholder="Debit ₹" type="number" step="0.01"
                                className="col-span-2 bg-background border border-border px-2 py-1.5 text-emerald-400 text-xs text-right"/>
                            <input value={ln.credit} onChange={e => setLine(i, "credit", e.target.value)}
                                data-testid={`journal-cr-${i}`}
                                placeholder="Credit ₹" type="number" step="0.01"
                                className="col-span-2 bg-background border border-border px-2 py-1.5 text-rose-400 text-xs text-right"/>
                            <input value={ln.memo} onChange={e => setLine(i, "memo", e.target.value)}
                                placeholder="Memo" className="col-span-3 bg-background border border-border px-2 py-1.5 text-foreground text-xs"/>
                            {lines.length > 2 && (
                                <button type="button" onClick={() => rmLine(i)} className="col-span-1 text-rose-400 hover:text-rose-300">
                                    <Trash2 className="w-3.5 h-3.5"/>
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <button type="button" onClick={addLine} className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline">+ add line</button>

                {/* Configurable extra fields (Phase 1a — fixed set; Phase 1b will be Super Admin–configurable) */}
                <div className="border-t border-card pt-3">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Optional references</div>
                    <div className="grid grid-cols-3 gap-2">
                        <input value={caseRef} onChange={e => setCaseRef(e.target.value)} placeholder="Case / Matter ref"
                            data-testid="journal-extra-case"
                            className="bg-background border border-border px-2 py-1.5 text-foreground text-xs"/>
                        <input value={clientRef} onChange={e => setClientRef(e.target.value)} placeholder="Client ref"
                            data-testid="journal-extra-client"
                            className="bg-background border border-border px-2 py-1.5 text-foreground text-xs"/>
                        <input value={costCenter} onChange={e => setCostCenter(e.target.value)} placeholder="Cost center / Branch"
                            data-testid="journal-extra-cost"
                            className="bg-background border border-border px-2 py-1.5 text-foreground text-xs"/>
                    </div>
                </div>

                <div className="flex items-center justify-between border-t border-card pt-3">
                    <div className="text-xs">
                        <span className="text-muted-foreground">Totals · </span>
                        <span className="text-emerald-400 font-mono">DR {fmtINR(totals.dr)}</span>
                        <span className="text-muted-foreground mx-2">·</span>
                        <span className="text-rose-400 font-mono">CR {fmtINR(totals.cr)}</span>
                        <span className="ml-3">{totals.balanced ? <span className="text-emerald-400">✓ balanced</span> : <span className="text-rose-400">✗ unbalanced</span>}</span>
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                        <button type="submit" disabled={!totals.balanced || saving}
                            data-testid="journal-submit"
                            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-30">
                            {saving ? "Posting…" : "Post"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------
function ReportsTab() {
    const [sub, setSub] = useState("tb");
    const [hasEntries, setHasEntries] = useState(null); // null=loading, true/false
    const [seeding, setSeeding] = useState(false);
    const checkEntries = useCallback(() => {
        api.get("/accounting/journal?limit=1").then(r => setHasEntries(r.data.length > 0)).catch(() => setHasEntries(true));
    }, []);
    useEffect(() => { checkEntries(); }, [checkEntries]);

    const seed = async () => {
        if (!window.confirm("Seed 8 realistic demo journal entries (partner capital, rent, retainer, salaries, court fees, GST etc.) so you can explore reports? This only runs on an empty ledger — your real data will not be touched.")) return;
        setSeeding(true);
        try {
            const { data } = await api.post("/accounting/seed-demo-data");
            toast.success(`Seeded ${data.seeded} demo entries · refresh below to see them`);
            checkEntries();
            // Force reload of TB component
            setSub("");
            setTimeout(() => setSub("tb"), 100);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSeeding(false); }
    };

    return (
        <div className="space-y-3" data-testid="acc-reports-tab">
            {hasEntries === false && (
                <div className="border border-primary/40 bg-primary/5 p-4 flex items-start gap-3" data-testid="demo-seed-banner">
                    <div className="text-2xl">🌱</div>
                    <div className="flex-1">
                        <div className="text-sm text-foreground font-medium">Your books are empty — try a quick demo</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            We&apos;ll seed 8 realistic Indian law-firm transactions (partner capital ₹5L, retainer received ₹2.36L with GST, salaries, court filing fees, software subscriptions etc.) so you can see Trial Balance, P&amp;L and Balance Sheet populated immediately.
                        </div>
                    </div>
                    <button onClick={seed} disabled={seeding} data-testid="demo-seed-btn"
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                        {seeding ? "Seeding…" : "Seed demo data"}
                    </button>
                </div>
            )}
            <div className="flex gap-1">
                {[{ k: "tb", l: "Trial Balance" }, { k: "pnl", l: "Profit & Loss" }, { k: "bs", l: "Balance Sheet" }].map(t => (
                    <button key={t.k} onClick={() => setSub(t.k)} data-testid={`reports-${t.k}`}
                        className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border ${sub === t.k ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {t.l}
                    </button>
                ))}
            </div>
            {sub === "tb"  && <TrialBalanceReport/>}
            {sub === "pnl" && <PnlReport/>}
            {sub === "bs"  && <BalanceSheetReport/>}
        </div>
    );
}

function TrialBalanceReport() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/accounting/reports/trial-balance").then(r => setData(r.data)).catch(e => toast.error(apiError(e)));
    }, []);
    if (!data) return <div className="text-muted-foreground text-sm">Loading trial balance…</div>;
    return (
        <div className="border border-border bg-card" data-testid="report-tb">
            <div className={`px-4 py-2 text-[10px] uppercase tracking-[0.16em] border-b border-border ${data.totals.balanced ? "text-emerald-400" : "text-rose-400"}`}>
                {data.totals.balanced ? "✓ Balanced" : "✗ Unbalanced"} · As of {fmtDate(data.as_of)}
            </div>
            <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <tr className="border-b border-border"><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Account</th><th className="text-right px-3 py-2">DR</th><th className="text-right px-3 py-2">CR</th><th className="text-right px-3 py-2">Balance</th></tr>
                </thead>
                <tbody>
                    {data.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No transactions yet</td></tr>}
                    {data.rows.map(r => (
                        <tr key={r.code} className="border-b border-card">
                            <td className="px-3 py-2 font-mono text-primary">{r.code}</td>
                            <td className="px-3 py-2 text-foreground">{r.name}</td>
                            <td className="px-3 py-2 text-right text-emerald-400 font-mono">{r.debit > 0 ? fmtINR(r.debit) : "—"}</td>
                            <td className="px-3 py-2 text-right text-rose-400 font-mono">{r.credit > 0 ? fmtINR(r.credit) : "—"}</td>
                            <td className="px-3 py-2 text-right text-foreground font-mono">{fmtINR(r.balance)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="font-mono text-foreground border-t-2 border-border">
                    <tr><td colSpan={2} className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Totals</td>
                        <td className="px-3 py-2 text-right text-emerald-400">{fmtINR(data.totals.debit)}</td>
                        <td className="px-3 py-2 text-right text-rose-400">{fmtINR(data.totals.credit)}</td>
                        <td className="px-3 py-2 text-right">—</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

function PnlReport() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/accounting/reports/pnl").then(r => setData(r.data)).catch(e => toast.error(apiError(e)));
    }, []);
    if (!data) return <div className="text-muted-foreground text-sm">Loading P&L…</div>;
    return (
        <div className="grid md:grid-cols-2 gap-3" data-testid="report-pnl">
            <Section title="Income" rows={data.income} total={data.total_income} colorClass="text-emerald-400"/>
            <Section title="Expenses" rows={data.expense} total={data.total_expense} colorClass="text-rose-400"/>
            <div className="md:col-span-2 border border-primary/40 bg-primary/5 px-4 py-3 text-sm flex justify-between">
                <span className="uppercase tracking-[0.16em] text-[10px] text-primary">Net Profit / Loss</span>
                <span className={`font-mono ${data.net_profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtINR(data.net_profit)}</span>
            </div>
        </div>
    );
}

function BalanceSheetReport() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/accounting/reports/balance-sheet").then(r => setData(r.data)).catch(e => toast.error(apiError(e)));
    }, []);
    if (!data) return <div className="text-muted-foreground text-sm">Loading balance sheet…</div>;
    return (
        <div className="grid md:grid-cols-3 gap-3" data-testid="report-bs">
            <Section title="Assets" rows={data.assets} total={data.total_assets} colorClass="text-emerald-400"/>
            <Section title="Liabilities" rows={data.liabilities} total={data.total_liabilities} colorClass="text-rose-400"/>
            <Section title="Equity" rows={data.equity} total={data.total_equity} colorClass="text-primary"/>
            <div className={`md:col-span-3 border ${data.is_balanced ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"} px-4 py-3 text-sm`}>
                {data.is_balanced ? "✓ Balance sheet balances" : "✗ Balance sheet does NOT balance — investigate"}
            </div>
        </div>
    );
}

function Section({ title, rows, total, colorClass }) {
    return (
        <div className="border border-border bg-card">
            <div className="px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border">{title}</div>
            <table className="w-full text-xs">
                <tbody>
                    {(!rows || rows.length === 0) && <tr><td colSpan={2} className="px-3 py-4 text-center text-muted-foreground">No data</td></tr>}
                    {rows && rows.map(r => (
                        <tr key={r.code} className="border-b border-card">
                            <td className="px-3 py-1.5 text-foreground"><span className="font-mono text-muted-foreground mr-2">{r.code}</span>{r.name}</td>
                            <td className={`px-3 py-1.5 text-right font-mono ${colorClass}`}>{fmtINR(r.amount)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="border-t-2 border-border">
                    <tr><td className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Total</td>
                        <td className={`px-3 py-2 text-right font-mono ${colorClass}`}>{fmtINR(total)}</td></tr>
                </tfoot>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Ledger
// ---------------------------------------------------------------------------
function LedgerTab() {
    const [coa, setCoa] = useState([]);
    const [code, setCode] = useState("");
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/accounting/coa").then(r => setCoa(r.data)).catch(() => {}); }, []);
    useEffect(() => {
        if (!code) { setData(null); return; }
        api.get(`/accounting/ledger/${code}`).then(r => setData(r.data)).catch(e => toast.error(apiError(e)));
    }, [code]);
    return (
        <div className="space-y-3" data-testid="acc-ledger-tab">
            <select value={code} onChange={e => setCode(e.target.value)}
                data-testid="ledger-select"
                className="bg-background border border-border px-3 py-2 text-foreground text-sm">
                <option value="">Select account…</option>
                {coa.map(a => <option key={a.id} value={a.code}>{a.code} · {a.name}</option>)}
            </select>
            {data && (
                <LedgerView data={data} code={code}/>
            )}
        </div>
    );
}

function LedgerView({ data, code }) {
    const ctrl = useTableControls(data.rows, {
        accessors: { narration: (r) => r.narration || r.memo || "" },
    });
    return (
        <div className="border border-border bg-card">
            <div className="px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border flex items-center gap-3">
                <span>Closing balance: <span className="text-primary font-mono">{fmtINR(data.closing_balance)}</span></span>
                <FilterChip ctrl={ctrl} className="ml-auto"/>
            </div>
            <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <tr className="border-b border-border">
                        <SortableTh field="date" ctrl={ctrl} className="text-left px-3 py-2">Date</SortableTh>
                        <SortableTh field="narration" ctrl={ctrl} className="text-left px-3 py-2">Narration</SortableTh>
                        <SortableTh field="debit" ctrl={ctrl} className="text-right px-3 py-2" align="right">DR</SortableTh>
                        <SortableTh field="credit" ctrl={ctrl} className="text-right px-3 py-2" align="right">CR</SortableTh>
                        <SortableTh field="running_balance" ctrl={ctrl} className="text-right px-3 py-2" align="right">Running</SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {ctrl.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No transactions for this account</td></tr>}
                    {ctrl.rows.map((r, i) => (
                        <tr key={r.entry_id || `${r.date}-${i}`} className="border-b border-card">
                            <td className="px-3 py-1.5 text-muted-foreground">{fmtDate(r.date)}</td>
                            <td className="px-3 py-1.5 text-foreground">{r.narration || r.memo}</td>
                            <td className="px-3 py-1.5 text-right text-emerald-400 font-mono">{r.debit > 0 ? fmtINR(r.debit) : "—"}</td>
                            <td className="px-3 py-1.5 text-right text-rose-400 font-mono">{r.credit > 0 ? fmtINR(r.credit) : "—"}</td>
                            <td className="px-3 py-1.5 text-right text-foreground font-mono">{fmtINR(r.running_balance)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------
// GST Config (OPTIONAL)
// ---------------------------------------------------------------------------
function GstTab() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const reload = () => api.get("/accounting/gst-config").then(r => setCfg(r.data));
    useEffect(() => { reload(); }, []);
    if (!cfg) return <div className="text-muted-foreground text-sm">Loading…</div>;

    const enabled = Boolean(cfg.gst_enabled);

    const save = async () => {
        setSaving(true);
        try { await api.put("/accounting/gst-config", cfg); toast.success("Saved"); reload(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const toggle = async () => {
        const next = { ...cfg, gst_enabled: !enabled };
        setCfg(next);
        try {
            await api.put("/accounting/gst-config", next);
            toast.success(!enabled ? "GST enabled — invoices will now include tax." : "GST disabled — invoices will not include tax.");
            reload();
        } catch (e) { toast.error(apiError(e)); reload(); }
    };

    return (
        <div className="border border-border bg-card p-5 max-w-2xl space-y-5" data-testid="acc-gst-tab">

            {/* ── Enable / Disable toggle ────────────────────────────────── */}
            <div className={`flex items-center justify-between px-4 py-3 border rounded ${enabled ? "border-green-500/40 bg-green-500/5" : "border-border bg-muted/30"}`}>
                <div>
                    <div className="text-sm font-medium text-foreground">GST Registration</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {enabled
                            ? "GST is active — all invoices will include tax lines."
                            : "GST is disabled — invoices raised without tax. Enable once your firm receives GST registration."}
                    </div>
                </div>
                <button
                    onClick={toggle}
                    data-testid="gst-toggle"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${enabled ? "bg-green-500" : "bg-muted-foreground/40"}`}
                >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
            </div>

            {/* ── GST fields — greyed out when disabled ─────────────────── */}
            <div className={`space-y-4 transition-opacity ${enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                <Field label="Company GSTIN" testid="gst-gstin">
                    <input value={cfg.company_gstin || ""} onChange={e => setCfg({...cfg, company_gstin: e.target.value.toUpperCase()})}
                        placeholder="e.g. 27ABCDE1234F1Z5"
                        disabled={!enabled}
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono disabled:cursor-not-allowed"/>
                </Field>
                <Field label="Company state code (2-digit)" testid="gst-state">
                    <input value={cfg.company_state_code || ""} onChange={e => setCfg({...cfg, company_state_code: e.target.value})}
                        placeholder="27" maxLength={2}
                        disabled={!enabled}
                        className="w-32 bg-background border border-border px-3 py-2 text-foreground text-sm font-mono disabled:cursor-not-allowed"/>
                </Field>
                <Field label="Default GST rate (%)" testid="gst-rate">
                    <input value={cfg.default_rate_pct || 18} type="number" onChange={e => setCfg({...cfg, default_rate_pct: parseFloat(e.target.value)})}
                        disabled={!enabled}
                        className="w-32 bg-background border border-border px-3 py-2 text-foreground text-sm disabled:cursor-not-allowed"/>
                </Field>
            </div>

            {/* ── Fields always editable ─────────────────────────────────── */}
            <div className="space-y-4">
                <Field label="Invoice number prefix" testid="gst-prefix">
                    <input value={cfg.invoice_prefix || ""} onChange={e => setCfg({...cfg, invoice_prefix: e.target.value})}
                        placeholder="INV" className="w-32 bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                </Field>
                <Field label="TDS rate (%) — §194J professional fees" testid="gst-tds">
                    <input value={cfg.tds_rate_pct || 10} type="number" onChange={e => setCfg({...cfg, tds_rate_pct: parseFloat(e.target.value)})}
                        className="w-32 bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                </Field>
            </div>

            <button onClick={save} disabled={saving} data-testid="gst-save"
                className="px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                {saving ? "Saving…" : "Save GST config"}
            </button>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            {children}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------
function PeriodsTab() {
    const [rows, setRows] = useState([]);
    const load = useCallback(() => api.get("/accounting/periods").then(r => setRows(r.data)).catch(e => toast.error(apiError(e))), []);
    useEffect(() => { load(); }, [load]);

    const toggle = async (p) => {
        const action = p.status === "closed" ? "reopen" : "close";
        if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} period ${p.period}?`)) return;
        try { await api.post(`/accounting/periods/${p.period}/${action}`); toast.success(`Period ${action}d`); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const ctrl = useTableControls(rows);

    return (
        <div className="space-y-3" data-testid="acc-periods-tab">
            <FilterChip ctrl={ctrl}/>
            <div className="border border-border bg-card">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh field="period" ctrl={ctrl} className="text-left px-3 py-2">Period</SortableTh>
                            <SortableTh field="status" ctrl={ctrl} className="text-left px-3 py-2">Status</SortableTh>
                            <SortableTh field="closed_at" ctrl={ctrl} className="text-left px-3 py-2">Closed at</SortableTh>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">No periods yet · post your first journal to auto-create one</td></tr>}
                        {ctrl.rows.map(p => (
                            <tr key={p.period} className="border-b border-card">
                                <td className="px-3 py-2 font-mono text-primary">{p.period}</td>
                                <td className="px-3 py-2 text-[10px] uppercase">{p.status === "closed" ? <span className="text-rose-400">Locked</span> : <span className="text-emerald-400">Open</span>}</td>
                                <td className="px-3 py-2 text-muted-foreground">{p.closed_at ? fmtDateTime(p.closed_at) : "—"}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => toggle(p)} data-testid={`period-toggle-${p.period}`}
                                        className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline inline-flex items-center gap-1">
                                        {p.status === "closed" ? <><Unlock className="w-3 h-3"/>Reopen</> : <><Lock className="w-3 h-3"/>Close</>}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------
function HealthTab() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/accounting/health").then(r => setData(r.data)).catch(e => toast.error(apiError(e))); }, []);
    if (!data) return <div className="text-muted-foreground text-sm">Running CA-grade health check…</div>;
    return (
        <div className="space-y-3" data-testid="acc-health-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Total Entries" value={data.summary.total_entries}/>
                <Kpi label="Open Periods" value={data.summary.open_periods}/>
                <Kpi label="Closed Periods" value={data.summary.closed_periods}/>
                <Kpi label="Books Balanced" value={data.summary.balanced ? "✓" : "✗"} highlight={data.summary.balanced ? "emerald" : "rose"}/>
            </div>
            <div className="space-y-2">
                {data.findings.map((f, i) => {
                    const sevColor = { critical: "rose-400", warning: "amber-400", info: "primary", ok: "emerald-400" }[f.severity] || "muted-foreground";
                    const Ic = f.severity === "ok" ? CheckCircle2 : AlertTriangle;
                    return (
                        <div key={f.code || i} className={`border border-border bg-card px-4 py-3 flex items-start gap-3`}>
                            <Ic className={`w-4 h-4 mt-0.5 text-${sevColor}`}/>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{f.code}</div>
                                <div className="text-sm text-foreground">{f.message}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Kpi({ label, value, highlight }) {
    const colorClass = highlight === "emerald" ? "text-emerald-400" : highlight === "rose" ? "text-rose-400" : "text-foreground";
    return (
        <div className="border border-border bg-card px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
            <div className={`text-2xl font-mono ${colorClass}`}>{value}</div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Invoices Tab — accounting-aware invoices that auto-post journal entries.
// First visit triggers a one-time legacy migration choice (OTP-protected).
// ---------------------------------------------------------------------------
function InvoicesTab() {
    const [migStatus, setMigStatus] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [showMigration, setShowMigration] = useState(false);
    const [gstEnabled, setGstEnabled] = useState(false);
    const [defaultGstRate, setDefaultGstRate] = useState(18);

    const loadInvoices = useCallback(() => {
        return api.get("/accounting/invoices").then(r => setInvoices(r.data)).catch(e => toast.error(apiError(e)));
    }, []);
    const loadMig = useCallback(() => {
        return api.get("/accounting/invoice-migration/status").then(r => setMigStatus(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            loadInvoices(),
            loadMig(),
            api.get("/clients").then(r => setClients(r.data)).catch(() => setClients([])),
            api.get("/cases?limit=500").then(r => setCases(r.data || [])).catch(() => setCases([])),
            api.get("/accounting/gst-config").then(r => {
                setGstEnabled(Boolean(r.data?.gst_enabled));
                setDefaultGstRate(r.data?.default_rate_pct || 18);
            }).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, [loadInvoices, loadMig]);

    const showMigrationBanner = migStatus?.needs_choice;
    const invoicesCtrl = useTableControls(invoices, {
        accessors: {
            client: (inv) => clients.find(c => c.id === inv.client_id)?.name || inv.client_id || "",
            date: (inv) => inv.created_at,
        },
    });

    const recordPayment = async (inv) => {
        const remaining = inv.amount - (inv.paid_amount || 0);
        const amt = window.prompt(`Record payment for ${inv.number}. Outstanding ₹${remaining.toFixed(2)}. Enter amount received:`, remaining.toFixed(2));
        if (!amt) return;
        const parsed = parseFloat(amt);
        if (!parsed || parsed <= 0) { toast.error("Invalid amount"); return; }
        try {
            await api.post(`/accounting/invoices/${inv.id}/record-payment`, { amount: parsed, method: "bank" });
            toast.success(`Payment ₹${parsed} recorded · journal posted`);
            loadInvoices();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-3" data-testid="acc-invoices-tab">
            {showMigrationBanner && (
                <div className="border border-primary/40 bg-primary/5 p-4" data-testid="migration-banner">
                    <div className="flex items-start gap-3">
                        <BookOpen className="w-5 h-5 text-primary mt-0.5"/>
                        <div className="flex-1">
                            <div className="text-sm text-foreground">📒 Set up your accounting books · {migStatus.legacy_count} existing invoices</div>
                            <div className="text-xs text-muted-foreground mt-1">Before we can run a clean ledger, you need to make a one-time choice about your pre-accounting invoices. <strong className="text-foreground">This decision is final and irreversible.</strong></div>
                        </div>
                        <button onClick={() => setShowMigration(true)} data-testid="open-migration-modal"
                            className="px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">
                            Decide now
                        </button>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    {invoices.length} invoices · {invoices.filter(i => i.status === "unpaid").length} unpaid · {invoices.filter(i => i.status === "paid").length} paid
                </div>
                <button onClick={() => setShowCreate(true)} data-testid="invoice-new-btn"
                    disabled={migStatus?.needs_choice}
                    title={migStatus?.needs_choice ? "Finalise the migration choice first" : ""}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-40">
                    <Plus className="w-3 h-3"/>New invoice
                </button>
            </div>

            <FilterChip ctrl={invoicesCtrl}/>
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh field="number" ctrl={invoicesCtrl} className="text-left px-3 py-2">Number</SortableTh>
                            <SortableTh field="date" ctrl={invoicesCtrl} className="text-left px-3 py-2">Date</SortableTh>
                            <SortableTh field="client" ctrl={invoicesCtrl} className="text-left px-3 py-2">Client</SortableTh>
                            <SortableTh field="amount" ctrl={invoicesCtrl} className="text-right px-3 py-2" align="right">Amount</SortableTh>
                            <SortableTh field="paid_amount" ctrl={invoicesCtrl} className="text-right px-3 py-2" align="right">Paid</SortableTh>
                            <SortableTh field="status" ctrl={invoicesCtrl} className="text-left px-3 py-2">Status</SortableTh>
                            <SortableTh field="source" ctrl={invoicesCtrl} className="text-left px-3 py-2">Source</SortableTh>
                            <SortableTh field="journal_id" ctrl={invoicesCtrl} className="text-left px-3 py-2">Journal</SortableTh>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>}
                        {!loading && invoicesCtrl.rows.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No invoices yet</td></tr>}
                        {!loading && invoicesCtrl.rows.map(inv => {
                            const client = clients.find(c => c.id === inv.client_id);
                            return (
                                <tr key={inv.id} className="border-b border-card">
                                    <td className="px-3 py-2 font-mono text-primary">{inv.number}</td>
                                    <td className="px-3 py-2 text-muted-foreground">{inv.created_at ? fmtDate(inv.created_at) : "—"}</td>
                                    <td className="px-3 py-2 text-foreground truncate max-w-[180px]">{client?.name || inv.client_id || "—"}</td>
                                    <td className="px-3 py-2 text-right text-foreground font-mono">{fmtINR(inv.amount)}</td>
                                    <td className="px-3 py-2 text-right text-emerald-400 font-mono">{fmtINR(inv.paid_amount || 0)}</td>
                                    <td className="px-3 py-2 text-[10px] uppercase">
                                        {inv.status === "paid" ? <span className="text-emerald-400">paid</span> :
                                         inv.status === "partial" ? <span className="text-amber-400">partial</span> :
                                         <span className="text-rose-400">unpaid</span>}
                                    </td>
                                    <td className="px-3 py-2 text-[10px] text-muted-foreground">
                                        {inv.source === "accounting" ? <span className="text-primary">accounting</span> :
                                         inv.source === "accounting_migrated" ? <span className="text-emerald-400">migrated</span> :
                                         <span className="text-muted-foreground">legacy</span>}
                                    </td>
                                    <td className="px-3 py-2 text-[10px] font-mono text-muted-foreground">
                                        {inv.acc_issue_entry_id ? "✓" : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {inv.status !== "paid" && inv.acc_issue_entry_id && (
                                            <button onClick={() => recordPayment(inv)}
                                                data-testid={`invoice-pay-${inv.id}`}
                                                className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline">Record payment</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreate && <NewInvoiceModal clients={clients} cases={cases} gstEnabled={gstEnabled} defaultGstRate={defaultGstRate}
                onClose={() => setShowCreate(false)}
                onCreated={(newClient) => {
                    if (newClient) {
                        // A new client was created inside the invoice modal — just update list, keep modal open
                        setClients(prev => [...prev, newClient]);
                        return;
                    }
                    // Invoice was submitted — close modal and refresh
                    setShowCreate(false);
                    loadInvoices();
                }} />}
            {showMigration && <MigrationModal status={migStatus} onClose={() => setShowMigration(false)} onCompleted={() => { setShowMigration(false); loadMig(); loadInvoices(); }} />}
        </div>
    );
}

function NewClientInlineModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: "", mobile: "", email: "", address: "",
        district: "", state: "", pincode: "", occupation: "", notes: "",
    });
    const [busy, setBusy] = useState(false);
    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const save = async (ev) => {
        ev?.preventDefault?.();
        if (!form.name.trim()) { toast.error("Client name is required"); return; }
        setBusy(true);
        try {
            const r = await api.post("/clients", form);
            toast.success(`Client "${r.data.name}" created`);
            onCreated(r.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-background/80 z-[60] flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={save} onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="new-client-inline-modal">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm text-foreground">New Client</h3>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Name *"><input required value={form.name} onChange={upd("name")} autoFocus data-testid="nc-name"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/></Field>
                    <Field label="Mobile"><input value={form.mobile} onChange={upd("mobile")} data-testid="nc-mobile"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/></Field>
                    <Field label="Email"><input type="email" value={form.email} onChange={upd("email")} data-testid="nc-email"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/></Field>
                    <Field label="Occupation"><input value={form.occupation} onChange={upd("occupation")} data-testid="nc-occupation"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/></Field>
                    <Field label="District"><input value={form.district} onChange={upd("district")} data-testid="nc-district"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/></Field>
                    <Field label="State"><input value={form.state} onChange={upd("state")} data-testid="nc-state"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/></Field>
                    <Field label="Pincode"><input value={form.pincode} onChange={upd("pincode")} maxLength={6} inputMode="numeric" data-testid="nc-pincode"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/></Field>
                </div>
                <Field label="Address"><textarea rows={2} value={form.address} onChange={upd("address")} data-testid="nc-address"
                    className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm resize-none"/></Field>
                <Field label="Notes"><textarea rows={2} value={form.notes} onChange={upd("notes")} data-testid="nc-notes"
                    className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm resize-none"/></Field>
                <div className="flex justify-end gap-2 pt-1 border-t border-border">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="nc-save"
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                        {busy ? "Saving…" : "Add client"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function NewInvoiceModal({ clients, cases = [], onClose, onCreated, gstEnabled = false, defaultGstRate = 18 }) {
    const [form, setForm] = useState({
        client_id: "", case_id: "",
        items: [{ description: "", amount: "", is_disbursement: false }],
        gst_rate_pct: gstEnabled ? defaultGstRate : 0,
        inter_state: false, due_date: "", notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [showNewClient, setShowNewClient] = useState(false);
    const [localClients, setLocalClients] = useState(clients);

    // Sync when parent refreshes clients
    useEffect(() => setLocalClients(clients), [clients]);

    const setItem = (i, k, v) => {
        const next = [...form.items];
        next[i] = { ...next[i], [k]: v };
        setForm({ ...form, items: next });
    };
    const addItem = () => setForm({ ...form, items: [...form.items, { description: "", amount: "", is_disbursement: false }] });
    const removeItem = (i) => {
        if (form.items.length === 1) return;
        setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
    };

    const filteredCases = useMemo(() =>
        form.client_id ? cases.filter(c => c.client_id === form.client_id) : cases,
        [cases, form.client_id]
    );

    const clientOptions = useMemo(() =>
        localClients.map(c => ({ value: c.id, label: c.name, sub: c.code || "" })),
        [localClients]
    );
    const caseOptions = useMemo(() =>
        filteredCases.map(c => ({
            value: c.id,
            label: c.title || c.original_suit_number || c.internal_number || c.id,
            sub: c.internal_number || "",
        })),
        [filteredCases]
    );

    const total = form.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const regularTotal = form.items.filter(i => !i.is_disbursement).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const disbTotal = form.items.filter(i => i.is_disbursement).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.client_id) { toast.error("Pick a client"); return; }
        if (total <= 0) { toast.error("Invoice total must be > 0"); return; }
        setSaving(true);
        try {
            await api.post("/accounting/invoices", {
                client_id: form.client_id,
                case_id: form.case_id || null,
                items: form.items
                    .filter(i => i.description && i.amount)
                    .map(i => ({
                        description: i.description,
                        amount: parseFloat(i.amount),
                        is_disbursement: Boolean(i.is_disbursement),
                    })),
                gst_rate_pct: gstEnabled ? (parseFloat(form.gst_rate_pct) || 0) : 0,
                inter_state: gstEnabled ? form.inter_state : false,
                due_date: form.due_date || null,
                notes: form.notes || null,
            });
            toast.success("Invoice issued · journal posted automatically");
            onCreated();
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <>
        {showNewClient && (
            <NewClientInlineModal
                onClose={() => setShowNewClient(false)}
                onCreated={(newClient) => {
                    setLocalClients(prev => [...prev, newClient]);
                    setForm(f => ({ ...f, client_id: newClient.id }));
                    setShowNewClient(false);
                    onCreated(newClient); // bubble up so parent list refreshes too
                }}
            />
        )}
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="invoice-create-modal">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg text-foreground">New invoice · auto-posts journal</h3>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
                </div>
                {!gstEnabled && (
                    <div className="text-xs bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-amber-400">
                        GST is disabled — invoice will be raised without tax. Enable GST in the GST Config tab once your firm is GST-registered.
                    </div>
                )}

                {/* Client */}
                <Field label="Client *">
                    <div className="flex gap-2 items-start">
                        <div className="flex-1">
                            <SearchableSelect
                                value={form.client_id}
                                onChange={v => setForm({ ...form, client_id: v, case_id: "" })}
                                options={clientOptions}
                                placeholder="Select or search client…"
                                testId="inv-client"
                                allowClear
                            />
                        </div>
                        <button type="button" onClick={() => setShowNewClient(true)} data-testid="inv-new-client-btn"
                            title="Create new client"
                            className="px-2.5 py-1.5 border border-border text-muted-foreground hover:border-primary hover:text-primary text-sm flex-shrink-0">
                            <Plus className="w-4 h-4"/>
                        </button>
                    </div>
                </Field>

                {/* Case (optional, cascades from client) */}
                <Field label={`Case / Matter ${form.client_id ? `(${filteredCases.length} available)` : "(select client first)"}`}>
                    <SearchableSelect
                        value={form.case_id}
                        onChange={v => setForm({ ...form, case_id: v })}
                        options={caseOptions}
                        placeholder={form.client_id ? "Select a case (optional)…" : "Select client first…"}
                        testId="inv-case"
                        disabled={!form.client_id}
                        allowClear
                    />
                </Field>

                {/* Line items */}
                <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Line items</div>
                    {form.items.map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                            <input value={it.description} onChange={e => setItem(i, "description", e.target.value)}
                                placeholder="Description"
                                className="col-span-6 bg-background border border-border px-2 py-1.5 text-foreground text-xs"
                                data-testid={`inv-item-desc-${i}`}/>
                            <input value={it.amount} onChange={e => setItem(i, "amount", e.target.value)}
                                placeholder="Amount ₹" type="number" step="0.01"
                                className="col-span-3 bg-background border border-border px-2 py-1.5 text-foreground text-xs text-right"
                                data-testid={`inv-item-amount-${i}`}/>
                            <label className="col-span-2 flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer"
                                title="Mark as court disbursement (e.g. filing fees, process fees)">
                                <input type="checkbox" checked={it.is_disbursement}
                                    onChange={e => setItem(i, "is_disbursement", e.target.checked)}
                                    className="accent-primary"
                                    data-testid={`inv-item-disb-${i}`}/>
                                Disb.
                            </label>
                            <button type="button" onClick={() => removeItem(i)}
                                className="col-span-1 text-muted-foreground hover:text-rose-400 flex justify-center"
                                data-testid={`inv-item-remove-${i}`}>
                                <X className="w-3 h-3"/>
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addItem}
                        className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline">
                        + add line
                    </button>
                    {disbTotal > 0 && (
                        <div className="text-[10px] text-muted-foreground border border-border/50 px-3 py-1.5 bg-background">
                            <span className="text-foreground">Disbursements</span> ₹{disbTotal.toFixed(2)} · credited to <span className="font-mono text-primary">4470 Disbursement Recovery</span>
                            &nbsp;·&nbsp;
                            <span className="text-foreground">Professional fees</span> ₹{regularTotal.toFixed(2)} · credited to <span className="font-mono text-primary">4400 Legal Services Revenue</span>
                        </div>
                    )}
                </div>

                {gstEnabled && (
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="GST rate %">
                            <input value={form.gst_rate_pct} onChange={e => setForm({...form, gst_rate_pct: e.target.value})} type="number" step="0.01"
                                className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                        </Field>
                        <label className="flex items-end gap-2 text-xs text-muted-foreground">
                            <input type="checkbox" checked={form.inter_state} onChange={e => setForm({...form, inter_state: e.target.checked})}
                                className="accent-primary"/>
                            Inter-state (IGST)
                        </label>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Due date">
                        <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"
                            data-testid="inv-due-date"/>
                    </Field>
                    <Field label="Notes">
                        <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                            placeholder="Internal note…"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </Field>
                </div>

                <div className="text-xs text-right text-primary font-mono">Total ₹ {total.toFixed(2)}</div>

                <div className="flex justify-end gap-2 pt-2 border-t border-card">
                    <button type="button" onClick={onClose}
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
                        Cancel
                    </button>
                    <button type="submit" disabled={saving} data-testid="inv-submit"
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                        {saving ? "Posting…" : "Issue invoice"}
                    </button>
                </div>
            </form>
        </div>
        </>
    );
}

function MigrationModal({ status, onClose, onCompleted }) {
    const [step, setStep] = useState("intro");   // intro | otp
    const [choice, setChoice] = useState("migrate");
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState("");
    const [sending, setSending] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const initiate = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/accounting/invoice-migration/initiate");
            toast.success(`OTP emailed to ${data.otp_sent_to}`);
            setOtpSent(true);
            setStep("otp");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSending(false); }
    };

    const confirm = async () => {
        if (otp.length < 6) { toast.error("Enter 6-digit OTP"); return; }
        setConfirming(true);
        try {
            const { data } = await api.post("/accounting/invoice-migration/confirm", { otp: otp.trim(), choice });
            if (choice === "migrate") {
                toast.success(`Migrated ${data.migrated_issues} invoices + ${data.migrated_payments} payments into your books`);
            } else {
                toast.success(`Archived ${data.archived} legacy invoices · books start clean`);
            }
            onCompleted();
        } catch (e) { toast.error(apiError(e)); }
        finally { setConfirming(false); }
    };

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="migration-modal">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-xl text-foreground">📒 Set up your accounting books</h3>
                        <p className="text-[11px] text-muted-foreground mt-1">You have <span className="text-primary">{status?.legacy_count}</span> existing invoices. Choose how they should appear in your new accounting ledger.</p>
                    </div>
                </div>

                {step === "intro" && (
                    <>
                        <div className={`border ${choice === "migrate" ? "border-primary bg-primary/5" : "border-border"} p-4 cursor-pointer`}
                            onClick={() => setChoice("migrate")} data-testid="choice-migrate">
                            <div className="flex items-center gap-3 mb-2">
                                <input type="radio" checked={choice === "migrate"} readOnly className="accent-primary"/>
                                <div className="text-sm text-foreground">Option B · Migrate existing invoices <span className="text-[10px] text-primary ml-1">⭐ RECOMMENDED</span></div>
                            </div>
                            <div className="text-xs text-muted-foreground ml-7">We&apos;ll back-fill journal entries for all {status?.legacy_count} invoices so your books show your complete history from day one. Trial Balance, P&amp;L, GSTR-1 will include past revenue.</div>
                            <div className="text-[10px] text-amber-400 ml-7 mt-1">⚠️ Risk: auto-derived journal entries can&apos;t perfectly reconstruct GST/TDS splits on invoices created before your GSTIN was set. You accept the migration &quot;as-is&quot;.</div>
                        </div>
                        <div className={`border ${choice === "retire" ? "border-primary bg-primary/5" : "border-border"} p-4 cursor-pointer`}
                            onClick={() => setChoice("retire")} data-testid="choice-retire">
                            <div className="flex items-center gap-3 mb-2">
                                <input type="radio" checked={choice === "retire"} readOnly className="accent-primary"/>
                                <div className="text-sm text-foreground">Option C · Start fresh</div>
                            </div>
                            <div className="text-xs text-muted-foreground ml-7">Past invoices stay as a read-only archive. New accounting module starts from zero. Past revenue will NOT appear in new reports.</div>
                            <div className="text-[10px] text-amber-400 ml-7 mt-1">⚠️ Risk: past invoices won&apos;t appear in TB/P&amp;L/GST returns — you&apos;ll need to file old returns from outside the platform.</div>
                        </div>
                        <div className="border border-rose-500/30 bg-rose-500/5 p-3 text-[11px] text-[#E0E0E0]">
                            <strong className="text-rose-400">Risk disclosure: </strong>
                            This choice is <strong>final and irreversible</strong>. All consequences and bookkeeping accuracy are solely the firm&apos;s responsibility — Inno Aim / Law OS provides no guarantee of GST or tax-filing correctness from migrated data.
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                            <button onClick={initiate} disabled={sending}
                                data-testid="migration-send-otp"
                                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                                {sending ? "Sending OTP…" : "Send OTP to confirm"}
                            </button>
                        </div>
                    </>
                )}

                {step === "otp" && (
                    <>
                        <div className="border border-primary/40 bg-primary/5 p-3 text-xs text-[#E0E0E0]">
                            We&apos;ve emailed a 6-digit OTP to your firm-owner inbox. Enter it below to commit your choice: <strong className="text-primary">{choice === "migrate" ? "Migrate" : "Start fresh"}</strong>.
                        </div>
                        <Field label="6-digit OTP">
                            <input value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} autoFocus
                                data-testid="migration-otp-input"
                                placeholder="123456"
                                className="w-40 bg-background border border-primary px-3 py-2 text-primary text-lg font-mono tracking-[0.3em] text-center"/>
                        </Field>
                        <div className="flex justify-between gap-2">
                            <button onClick={() => setStep("intro")} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">← Back</button>
                            <div className="flex gap-2">
                                <button onClick={initiate} disabled={sending} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Resend OTP</button>
                                <button onClick={confirm} disabled={confirming || otp.length < 6}
                                    data-testid="migration-confirm"
                                    className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                                    {confirming ? "Committing…" : `Verify & ${choice === "migrate" ? "migrate" : "retire"}`}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Story Mode — chronological walkthrough of demo-seeded entries with
// business narrative + accounting concept. Helps new tenants learn
// double-entry by reading their own books like a story.
// ---------------------------------------------------------------------------
function StoryModeTab() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(0);

    useEffect(() => {
        api.get("/accounting/journal?source=demo_seed&limit=50")
            .then(r => {
                // sort by story_step ascending (chronological)
                const sorted = (r.data || []).slice().sort((a, b) => {
                    const sa = (a.extra && a.extra.story_step) || 99;
                    const sb = (b.extra && b.extra.story_step) || 99;
                    return sa - sb;
                });
                setEntries(sorted);
            })
            .catch(() => setEntries([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-muted-foreground text-sm">Loading the story…</div>;
    if (entries.length === 0) {
        return (
            <div className="border border-primary/40 bg-primary/5 p-6 text-center" data-testid="story-empty">
                <div className="text-2xl mb-2">📖</div>
                <div className="text-foreground">No story to show yet</div>
                <div className="text-xs text-muted-foreground mt-1">Story mode walks you through the demo-seeded entries. Go to <span className="text-primary">Reports</span> and click &quot;Seed demo data&quot; first.</div>
            </div>
        );
    }

    const e = entries[step];
    const story = (e.extra && e.extra.story_text) || "(no story for this entry)";
    const concept = (e.extra && e.extra.accounting_concept) || "";
    const total = entries.length;
    const pct = ((step + 1) / total) * 100;

    return (
        <div className="space-y-4" data-testid="acc-story-tab">
            {/* Progress + step navigation */}
            <div className="border border-border bg-card p-4 space-y-3">
                <div className="flex justify-between items-center">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Step {step + 1} of {total}</span>
                    <span className="text-[10px] text-muted-foreground">{fmtDate(e.date)}</span>
                </div>
                <div className="h-1 bg-card">
                    <div className="h-1 bg-primary transition-all" style={{ width: `${pct}%` }}/>
                </div>

                {/* Narrative */}
                <div className="bg-background border-l-4 border-primary px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-primary mb-1">📖 What happened</div>
                    <div className="text-sm text-foreground leading-relaxed">{story}</div>
                </div>

                {/* Accounting concept */}
                {concept && (
                    <div className="bg-background border-l-4 border-emerald-500 px-4 py-3">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-400 mb-1">💡 Accounting concept</div>
                        <div className="text-sm text-[#E0E0E0] leading-relaxed">{concept}</div>
                    </div>
                )}

                {/* The actual journal entry */}
                <div className="border border-border bg-background">
                    <div className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border">
                        Journal entry · {e.narration}
                    </div>
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            <tr className="border-b border-card"><th className="text-left px-3 py-1.5">Account</th><th className="text-right px-3 py-1.5">DR</th><th className="text-right px-3 py-1.5">CR</th><th className="text-left px-3 py-1.5">Memo</th></tr>
                        </thead>
                        <tbody>
                            {e.lines.map((ln, i) => (
                                <tr key={`${ln.account_code}-${i}`}>
                                    <td className="px-3 py-1.5 font-mono text-primary">{ln.account_code}</td>
                                    <td className="px-3 py-1.5 text-right text-emerald-400 font-mono">{ln.debit > 0 ? fmtINR(ln.debit) : "—"}</td>
                                    <td className="px-3 py-1.5 text-right text-rose-400 font-mono">{ln.credit > 0 ? fmtINR(ln.credit) : "—"}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{ln.memo}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Step controls */}
                <div className="flex justify-between items-center pt-2">
                    <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
                        data-testid="story-prev"
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-border text-muted-foreground hover:text-foreground disabled:opacity-30">
                        ← Previous
                    </button>
                    <div className="flex gap-1">
                        {entries.map((_, i) => (
                            <button key={i} onClick={() => setStep(i)}
                                className={`w-2 h-2 rounded-full ${i === step ? "bg-primary" : "bg-border hover:bg-muted-foreground"}`}/>
                        ))}
                    </div>
                    {step < total - 1 ? (
                        <button onClick={() => setStep(s => Math.min(total - 1, s + 1))}
                            data-testid="story-next"
                            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10">
                            Next →
                        </button>
                    ) : (
                        <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-emerald-400">✓ Story complete</span>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Super-admin Edit Request Banner — shown at top when one or more
// admin edit requests are pending tenant approval. Click approve → enter OTP.
// ---------------------------------------------------------------------------
function AdminEditRequestBanner() {
    const [reqs, setReqs] = useState([]);
    const [otpFor, setOtpFor] = useState(null);
    const [otp, setOtp] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(() => {
        api.get("/accounting/admin-edit-requests").then(r => setReqs(r.data)).catch(() => setReqs([]));
    }, []);
    useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

    if (!reqs || reqs.length === 0) return null;

    const reject = async (id) => {
        if (!window.confirm("Reject this admin edit request? The super admin will be notified and no change will be made.")) return;
        try { await api.post(`/accounting/admin-edit-requests/${id}/reject`); toast.success("Rejected"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const approve = async (id) => {
        if (!otp || otp.length < 4) { toast.error("Enter the 6-digit OTP from your email"); return; }
        setSubmitting(true);
        try {
            await api.post(`/accounting/admin-edit-requests/${id}/approve`, { otp: otp.trim() });
            toast.success("Edit applied to your books · all firm admins emailed");
            setOtpFor(null); setOtp(""); load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="space-y-2" data-testid="admin-edit-banner">
            {reqs.map(r => (
                <div key={r.id} className="border border-amber-500/40 bg-amber-500/5 p-4">
                    <div className="flex items-start gap-3">
                        <ShieldAlert className="w-5 h-5 text-amber-400 mt-0.5 shrink-0"/>
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-amber-400">Super admin is requesting an edit to your books · pending your approval</div>
                            <div className="text-sm text-foreground mt-1">{r.summary}</div>
                            <div className="text-[11px] text-muted-foreground mt-1"><strong className="text-primary">Reason:</strong> {r.reason}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">From {r.requested_by_email} · expires {fmtDateTime(r.expires_at)}</div>

                            {otpFor === r.id ? (
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <Mail className="w-3.5 h-3.5 text-primary"/>
                                    <span className="text-[11px] text-muted-foreground">Enter the 6-digit OTP we emailed to {r.owner_email}:</span>
                                    <input value={otp} onChange={e => setOtp(e.target.value)} autoFocus maxLength={6}
                                        placeholder="123456"
                                        data-testid={`approve-otp-input-${r.id}`}
                                        className="w-28 bg-background border border-primary px-3 py-1.5 text-primary text-sm font-mono tracking-[0.3em] text-center"/>
                                    <button onClick={() => approve(r.id)} disabled={submitting}
                                        data-testid={`approve-otp-submit-${r.id}`}
                                        className="px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                                        {submitting ? "Verifying…" : "Verify & commit"}
                                    </button>
                                    <button onClick={() => { setOtpFor(null); setOtp(""); }} className="text-[10px] uppercase text-muted-foreground hover:text-foreground">
                                        <X className="w-3.5 h-3.5"/>
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <button onClick={() => setOtpFor(r.id)}
                                        data-testid={`admin-edit-approve-${r.id}`}
                                        className="px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">Approve with OTP</button>
                                    <button onClick={() => reject(r.id)}
                                        data-testid={`admin-edit-reject-${r.id}`}
                                        className="px-3 py-1.5 border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-[10px] uppercase tracking-[0.16em]">Reject</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}



/* ───────────────────────────────────────────────────────────────────────
 * CA-Pack download button — header action
 * ─────────────────────────────────────────────────────────────────────── */
function CaPackDownloadButton() {
    const [busy, setBusy] = useState(false);

    function _prevFy() {
        const d = new Date();
        const ist = new Date(d.getTime() + (5.5 - (-d.getTimezoneOffset() / 60)) * 3600 * 1000);
        const m = ist.getUTCMonth() + 1;
        const y = ist.getUTCFullYear();
        if (m >= 4) return `${y - 1}-${String(y).slice(-2)}`;
        return `${y - 2}-${String(y - 1).slice(-2)}`;
    }

    const handleDownload = async () => {
        const fy = window.prompt("Download CA Pack for which Financial Year? (e.g. 2025-26)", _prevFy());
        if (!fy || !/^\d{4}-\d{2}$/.test(fy)) {
            if (fy !== null) toast.error("Invalid FY — use the format 2025-26");
            return;
        }
        setBusy(true);
        try {
            const apiBase = process.env.REACT_APP_BACKEND_URL || "";
            const res = await fetch(`${apiBase}/api/tenant/ca-pack/download?fy=${encodeURIComponent(fy)}`, { credentials: "include" });
            if (!res.ok) { const txt = await res.text(); throw new Error(txt || `HTTP ${res.status}`); }
            const blob = await res.blob();
            const cd = res.headers.get("Content-Disposition") || "";
            const m = cd.match(/filename="?([^";]+)"?/);
            const filename = (m && m[1]) || `Law-OS-CA-Pack-FY${fy}.zip`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`CA Pack downloaded · ${filename}`);
        } catch (e) {
            toast.error(`Download failed: ${String(e).slice(0, 200)}`);
        } finally { setBusy(false); }
    };

    const handleEmail = async () => {
        const fy = window.prompt("Email CA Pack to your firm owner + accountants. FY? (e.g. 2025-26)", _prevFy());
        if (!fy || !/^\d{4}-\d{2}$/.test(fy)) {
            if (fy !== null) toast.error("Invalid FY — use 2025-26");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/tenant/ca-pack/email?fy=${encodeURIComponent(fy)}`);
            if (data.sent) toast.success(`CA Pack emailed to ${(data.recipients || []).length} recipient(s)`);
            else toast.error(`Not sent · ${data.skipped_reason || "unknown reason"}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="flex items-center gap-2" data-testid="ca-pack-actions">
            <button onClick={handleDownload} disabled={busy} data-testid="ca-pack-download"
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                <Download className="w-3.5 h-3.5"/>{busy ? "Building…" : "Download CA Pack"}
            </button>
            <button onClick={handleEmail} disabled={busy} data-testid="ca-pack-email"
                title="Email a copy to firm owner + accountants"
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-border text-foreground hover:border-primary text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                <Mail className="w-3.5 h-3.5"/>Email
            </button>
        </div>
    );
}

/* ───────────────────────────────────────────────────────────────────────
 * Quick Legal Entry modal — pre-built templates for law-firm transactions
 * ─────────────────────────────────────────────────────────────────────── */
function LegalEntryModal({ onClose, onPosted }) {
    const [catalog, setCatalog] = useState(null);
    const [selected, setSelected] = useState(null);
    const [amount, setAmount] = useState("");
    const [partyName, setPartyName] = useState("");
    const [caseId, setCaseId] = useState("");
    const [narration, setNarration] = useState("");
    const [paymentMode, setPaymentMode] = useState("bank");
    const [cases, setCases] = useState([]);
    const [posting, setPosting] = useState(false);

    useEffect(() => {
        api.get("/accounting/legal/templates").then(r => setCatalog(r.data)).catch(e => toast.error(apiError(e)));
        api.get("/cases?limit=200").then(r => setCases(r.data || [])).catch(() => {});
    }, []);

    const grouped = useMemo(() => {
        if (!catalog) return {};
        const g = {};
        for (const t of catalog.templates) {
            g[t.bucket] = g[t.bucket] || [];
            g[t.bucket].push(t);
        }
        return g;
    }, [catalog]);

    const submit = async () => {
        if (!selected) { toast.error("Pick a template"); return; }
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
        setPosting(true);
        try {
            const payload = {
                template_code: selected.code,
                amount: amt,
                payment_mode: paymentMode,
                party_name: selected.needs_party ? (partyName || null) : null,
                case_id: selected.needs_case ? (caseId || null) : null,
                narration: narration || null,
            };
            await api.post("/accounting/legal/post", payload);
            toast.success(`${selected.label} · ₹${amt.toLocaleString("en-IN")} posted`);
            onPosted();
        } catch (e) { toast.error(apiError(e)); }
        finally { setPosting(false); }
    };

    if (!catalog) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="legal-entry-modal">
            <div className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                    <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-primary"/>
                        <h3 className="text-base font-semibold text-foreground">Quick Legal Entry</h3>
                    </div>
                    <button onClick={onClose} data-testid="legal-entry-close" className="text-muted-foreground hover:text-foreground">
                        <X className="w-4 h-4"/>
                    </button>
                </div>
                <div className="p-5 space-y-5">
                    {!selected ? (
                        <div className="space-y-4">
                            <p className="text-xs text-muted-foreground">Pre-built journal entries for the 9 most common law-firm transactions. The required COA accounts (Client Trust, Counsel Fees, etc.) are auto-created on first use.</p>
                            {Object.entries(grouped).map(([bucket, items]) => (
                                <div key={bucket} data-testid={`legal-bucket-${bucket}`}>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 font-semibold">{bucket}</div>
                                    <div className="grid sm:grid-cols-2 gap-2">
                                        {items.map(t => (
                                            <button key={t.code} onClick={() => setSelected(t)}
                                                data-testid={`legal-tpl-${t.code}`}
                                                className="text-left p-3 border border-border hover:border-primary hover:bg-primary/5 rounded-lg transition-colors">
                                                <div className="font-medium text-foreground text-sm">{t.label}</div>
                                                <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{t.description}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">{selected.bucket}</div>
                                        <div className="mt-1 text-foreground font-medium" data-testid="legal-selected-label">{selected.label}</div>
                                        <div className="text-xs text-muted-foreground mt-1">{selected.description}</div>
                                    </div>
                                    <button onClick={() => setSelected(null)}
                                        data-testid="legal-change-template"
                                        className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-primary">Change</button>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Amount (₹)</span>
                                    <input type="number" min="0" step="0.01" value={amount}
                                        onChange={e => setAmount(e.target.value)}
                                        data-testid="legal-amount"
                                        placeholder="0.00"
                                        className="mt-1 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm font-mono text-foreground rounded-lg"/>
                                </label>
                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Payment mode</span>
                                    <select value={paymentMode} onChange={e => setPaymentMode(e.target.value)}
                                        data-testid="legal-payment-mode"
                                        className="mt-1 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground rounded-lg">
                                        <option value="bank">Bank — Operating Account</option>
                                        <option value="cash">Cash in Hand</option>
                                    </select>
                                </label>
                                {selected.needs_party && (
                                    <label className="block sm:col-span-2">
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Party (client / counsel name)</span>
                                        <input type="text" value={partyName} onChange={e => setPartyName(e.target.value)}
                                            data-testid="legal-party"
                                            placeholder="e.g. Acme Corp / Adv. Senior Counsel"
                                            className="mt-1 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground rounded-lg"/>
                                    </label>
                                )}
                                {selected.needs_case && (
                                    <label className="block sm:col-span-2">
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Linked litigation (optional)</span>
                                        <div className="mt-1">
                                            <SearchableSelect value={caseId} onChange={setCaseId}
                                                placeholder="— none —" allowClear testId="legal-case"
                                                options={cases.map(c => ({
                                                    value: c.id,
                                                    label: c.title,
                                                    sub: c.court_case_number || c.id.slice(-6),
                                                }))}/>
                                        </div>
                                    </label>
                                )}
                                <label className="block sm:col-span-2">
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Memo (optional)</span>
                                    <input type="text" value={narration} onChange={e => setNarration(e.target.value)}
                                        data-testid="legal-narration"
                                        placeholder="Hearing dt 12-Mar · Stamp duty for vakalat etc."
                                        className="mt-1 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground rounded-lg"/>
                                </label>
                            </div>

                            <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 font-semibold">Will post this double-entry:</div>
                                <table className="w-full font-mono">
                                    <tbody>
                                        {selected.lines.map((l, i) => (
                                            <tr key={i}>
                                                <td className="py-0.5 text-foreground">{l.account_code === "BANK" ? (paymentMode === "cash" ? "1200 Cash" : "1100 Bank") : l.account_code}</td>
                                                <td className="text-right text-emerald-600 w-24">{l.direction === "dr" ? `DR ${amount || "—"}` : ""}</td>
                                                <td className="text-right text-rose-600 w-24">{l.direction === "cr" ? `CR ${amount || "—"}` : ""}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex justify-end gap-2">
                                <button onClick={onClose} data-testid="legal-cancel"
                                    className="px-4 py-2 border border-border text-foreground hover:border-primary text-[10px] uppercase tracking-[0.16em]">Cancel</button>
                                <button onClick={submit} disabled={posting || !amount}
                                    data-testid="legal-post"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                                    {posting ? "Posting…" : "Post entry"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ───────────────────────────────────────────────────────────────────────
 * Payment Gateway Auto-Reconciliation — match Razorpay payments to invoices
 * ─────────────────────────────────────────────────────────────────────── */
function ReconciliationTab() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);
    const [filter, setFilter] = useState("");

    const load = useCallback(async () => {
        try {
            const q = filter ? `?status=${filter}` : "";
            const { data } = await api.get(`/accounting/reconciliation/list${q}`);
            setData(data);
        } catch (e) { toast.error(apiError(e)); }
    }, [filter]);

    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    async function sync() {
        setBusy("sync");
        try {
            const { data: r } = await api.post("/accounting/reconciliation/sync?days=30");
            toast.success(`Synced · pulled ${r.pulled}, new ${r.inserted}`);
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    }

    async function autoMatch() {
        setBusy("match");
        try {
            const { data: r } = await api.post("/accounting/reconciliation/auto-match");
            toast.success(`Auto-matched ${r.auto_matched} · ambiguous ${r.ambiguous}`);
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    }

    async function postOne(pid) {
        if (!window.confirm("Post journal entry for this payment (Bank, Gateway Fees, GST Input, Trade Receivable)?")) return;
        setBusy(pid);
        try {
            await api.post(`/accounting/reconciliation/post/${pid}`);
            toast.success("Journal posted");
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    }

    async function dispute(pid) {
        if (!window.confirm("Mark this payment as disputed?")) return;
        try {
            await api.post("/accounting/reconciliation/manual-match",
                            { payment_id: pid, action: "reject_dispute" });
            toast.success("Marked disputed");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    }

    if (!data) return <div className="text-sm text-muted-foreground" data-testid="recon-loading">Loading reconciliation…</div>;
    const s = data.summary;

    return (
        <div className="space-y-4" data-testid="recon-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs">
                    {["", "unmatched", "auto_matched", "manual_matched", "ambiguous", "disputed", "posted"].map(st => (
                        <button key={st} onClick={() => setFilter(st)}
                            data-testid={`recon-filter-${st || "all"}`}
                            className={`px-2.5 py-1 rounded-md ${filter === st ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:border-primary"}`}>
                            {st || "All"} · {st === "" ? Object.values(s).reduce((a,b)=>a+b,0) : s[st] ?? 0}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={sync} disabled={busy === "sync"} data-testid="recon-sync"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-40">
                        {busy === "sync" ? "Syncing…" : "Sync from Razorpay"}
                    </button>
                    <button onClick={autoMatch} disabled={busy === "match"} data-testid="recon-auto-match"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 text-[10px] uppercase tracking-[0.16em] disabled:opacity-40">
                        {busy === "match" ? "Matching…" : "Auto-match"}
                    </button>
                </div>
            </div>

            <div className="rounded-xl border border-border overflow-x-auto">
                {data.rows.length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground" data-testid="recon-empty">
                        No payments to reconcile. Click <strong>Sync from Razorpay</strong> to pull the last 30 days.
                    </div>
                ) : (
                    <ReconciliationTable rows={data.rows} busy={busy} postOne={postOne} dispute={dispute}/>
                )}
            </div>
        </div>
    );
}

function ReconciliationTable({ rows, busy, postOne, dispute }) {
    const ctrl = useTableControls(rows, {
        accessors: {
            payment: (r) => r.razorpay_payment_id,
            fee: (r) => Number(r.fee_inr || 0) + Number(r.tax_inr || 0),
            matched: (r) => r.matched_invoice_number || "",
        },
    });
    return (
        <>
        <FilterChip ctrl={ctrl} className="m-3"/>
        <table className="w-full text-sm">
            <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground bg-muted/30">
                    <SortableTh field="payment" ctrl={ctrl} className="text-left px-3 py-2">Payment</SortableTh>
                    <SortableTh field="amount_inr" ctrl={ctrl} className="text-right px-2 py-2" align="right">Amount</SortableTh>
                    <SortableTh field="fee" ctrl={ctrl} className="text-right px-2 py-2" align="right">Fee + GST</SortableTh>
                    <SortableTh field="match_status" ctrl={ctrl} className="text-left px-2 py-2">Status</SortableTh>
                    <SortableTh field="matched" ctrl={ctrl} className="text-left px-2 py-2">Matched invoice</SortableTh>
                    <th className="text-right px-3 py-2">Action</th>
                </tr>
            </thead>
            <tbody>
                {ctrl.rows.map(r => (
                                <tr key={r.id} className="border-t border-border/50" data-testid={`recon-row-${r.razorpay_payment_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-mono text-[11px] text-foreground">{r.razorpay_payment_id}</div>
                                        <div className="text-[10px] text-muted-foreground">{r.method} · {r.captured_at && fmtDate(new Date(r.captured_at * 1000))}</div>
                                    </td>
                                    <td className="text-right font-mono">₹{r.amount_inr.toLocaleString("en-IN")}</td>
                                    <td className="text-right font-mono text-rose-600">₹{(r.fee_inr + r.tax_inr).toLocaleString("en-IN")}</td>
                                    <td>
                                        <span className={`text-[10px] uppercase tracking-wider font-semibold ${
                                            r.match_status === "auto_matched" || r.match_status === "manual_matched" ? "text-emerald-600"
                                                : r.match_status === "ambiguous" ? "text-amber-600"
                                                : r.match_status === "disputed" ? "text-rose-600"
                                                : "text-muted-foreground"
                                        }`}>{r.match_status}</span>
                                        {r.posted_at && <span className="ml-2 text-[10px] text-emerald-600">· posted</span>}
                                    </td>
                                    <td className="text-[11px] font-mono">{r.matched_invoice_number || (r.candidate_count ? `${r.candidate_count} candidates` : "—")}</td>
                                    <td className="text-right px-3 py-2">
                                        {!r.posted_at && (r.match_status === "auto_matched" || r.match_status === "manual_matched") && (
                                            <button onClick={() => postOne(r.razorpay_payment_id)} disabled={busy === r.razorpay_payment_id}
                                                data-testid={`recon-post-${r.razorpay_payment_id}`}
                                                className="px-2 py-1 bg-primary text-primary-foreground rounded text-[10px] uppercase tracking-wider font-semibold hover:opacity-90 disabled:opacity-40">
                                                {busy === r.razorpay_payment_id ? "…" : "Post"}
                                            </button>
                                        )}
                                        {!r.posted_at && r.match_status !== "disputed" && (
                                            <button onClick={() => dispute(r.razorpay_payment_id)}
                                                data-testid={`recon-dispute-${r.razorpay_payment_id}`}
                                                className="ml-1 px-2 py-1 border border-rose-500 text-rose-600 rounded text-[10px] uppercase tracking-wider font-semibold hover:bg-rose-50">
                                                Dispute
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
        </>
    );
}

